/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { VISUAL_HEADER_TYPES } from 'modules/dashboard/constants';
import { TIME_PATTERN, YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';
import { formatDate } from 'modules/dashboard/functions';
import { models } from 'powerbi-client';
import { daysOrder, monthsOrder } from './constants';
import mapper from './mapper';

/**
 * Checks whether a given string matches the "Month Year" pattern.
 *
 * The pattern must follow the format: `xxx YYYY`, where:
 * - `xxx` is a three-letter month abbreviation with the first letter uppercase (e.g., "Jan", "Feb").
 * - `YYYY` is a four-digit year (e.g., "2025").
 *
 * Example of valid input: `"Jul 2025"`.
 *
 * @param {string} value - The input string to validate.
 * @returns {boolean} `true` if the input matches the "Month Year" pattern, otherwise `false`.
 */
function isMonthYearPatternList(value) {
  const monthYearRegex = /^[A-Z][a-z]{2} \d{4}$/;
  return typeof value === 'string' && monthYearRegex.test(value);
}

function formatIfValueIsDate(str) {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/; // regex to check if the value is a date formatted like yyyy-MM-DD

  if (dateRegex.test(str.replace(/^"|"$/g, ''))) {
    return str.replaceAll('-', '/');
  }
  return str;
}

const withSingleHeader = (result, fileName) => {
  if (!result?.data) return '';

  const rows = result.data.split('\r\n');
  if (rows.length === 0) return '';

  const rawHeaders = rows[0].split(',');

  const metrics = mapper[fileName]?.metrics || [];

  if (metrics.length === 0) {
    return [rawHeaders.join(','), ...rows.slice(1)].join('\r\n');
  }

  const headerMap = {};
  rawHeaders.forEach((h, i) => {
    if (!headerMap[h]) headerMap[h] = [];
    headerMap[h].push(i);
  });

  const usageTracker = {};

  const finalIndexes = metrics
    .map((metric) => {
      const indexes = headerMap[metric];

      if (!indexes || indexes.length === 0) {
        return -1;
      }

      const used = usageTracker[metric] || 0;

      if (used >= indexes.length) {
        return -1;
      }

      usageTracker[metric] = used + 1;

      return indexes[used];
    })
    .filter((i) => i !== -1);

  const csvHeader = metrics.map((item) => item.charAt(0).toUpperCase() + item.slice(1)).join(',');
  // Build rows
  const csvRows = rows.slice(1).map((row) => {
    const cols = row.split(',');
    return finalIndexes.map((i) => cols[i] || '').join(',');
  });
  const normalizedRow = csvRows.map((csvRow) => (csvRow.endsWith(',') ? `${csvRow} ` : csvRow));
  //
  return [csvHeader, ...normalizedRow].join('\r\n');
};

const withGroupedDoubleHeaders = (result, fileName) => {
  let csv = '';
  const parsedData = result?.data.split('\r\n').slice(1);

  const metrics = {};
  const groupedData = {};
  parsedData.forEach((item) => {
    const row = item.split(',');

    // record the metrics and sub headings
    if (metrics[row[2]] === undefined) {
      metrics[row[2]] = '';
    }
    // add the metric if not included
    if (!metrics[row[2]].includes(row[3])) metrics[row[2]] += `${row[3]},`;

    // map values to metrics and sub columns
    if (groupedData[row[0]] === undefined) {
      groupedData[row[0]] = {};
    }
    if (groupedData[row[0]][row[1]] === undefined) {
      groupedData[row[0]][row[1]] = {};
    }
    // for the labels
    if (groupedData[row[0]][row[1]][row[2]] === undefined) {
      groupedData[row[0]][row[1]][row[2]] = {};
    }

    groupedData[row[0]][row[1]][row[2]][row[3]] = item.replace(
      `${row[0]},${row[1]},${row[2]},${row[3]}`,
      ''
    );
  });

  // set the headers
  let parentHeaders = 'Metric,';
  let subHeaders = `${mapper[fileName].groupBy},`;
  Object.keys(metrics).forEach((metric) => {
    if (metric) {
      if (metric === 'undefined') return;
      // get the number of sub headers and merge the parent
      parentHeaders += metric + ','.repeat(metrics[metric].split(',').length - 1);
      subHeaders += metrics[metric];
    }
  });

  csv += `${parentHeaders}\r\n`;
  csv += `${subHeaders}\r\n`;

  const rowWidth = Object.keys(metrics).length;

  const groupDataList = Object.keys(groupedData);

  // if metric is lags , sort ascending order
  if (mapper[fileName].groupBy === 'lags') {
    groupDataList.sort((a, b) => {
      if (a === '' && b === '') return 0;
      if (a === '') return 1;
      if (b === '') return -1;
      return Number(a) - Number(b);
    });
  } else {
    groupDataList.sort((a, b) => new Date(a) - new Date(b));
  }

  // concatenate the data
  groupDataList.forEach((item) => {
    if (item) {
      csv += item + ','.repeat(rowWidth);
      csv += '\r\n';
      Object.keys(groupedData[item]).forEach((m) => {
        // if values are not inline to the columns this logic could be the issue
        const currentMetrics = Object.keys(groupedData[item][m]);
        // mark the number of the iteration and look if the values for that index is there in the current metric list
        // if not add a placeholder to reserve that space
        csv += `${m}`;
        currentMetrics.forEach((currentMetric) => {
          // use the metrics array to preserve the fields order
          metrics[currentMetric]
            .slice(0, -1)
            .split(',')
            .forEach((orderVal) => {
              if (groupedData[item][m][currentMetric][orderVal]) {
                csv += groupedData[item][m][currentMetric][orderVal];
              } else {
                csv += ',';
              }
            });
        });
        csv += '\r\n';
      });
    }
  });

  return csv;
};

const withDoubleHeaders2 = (result, fileName) => {
  let csv = '';
  // Split data set by new line character and create a new array removing the first row
  const parsedData = result?.data.split('\r\n').slice(1);

  const metrics = {};
  const groupedData = {};
  parsedData.forEach((item) => {
    const row = item.split(',');

    const key = formatIfValueIsDate(row[0]);

    // record the metrics and sub headings
    if (metrics[row[1]] === undefined) {
      metrics[row[1]] = '';
    }
    // add the metric if not included
    if (!metrics[row[1]].includes(row[2])) metrics[row[1]] += `${row[2]},`;

    // map values to metrics and sub columns
    if (groupedData[key] === undefined) {
      groupedData[key] = {};
    }
    if (groupedData[key][row[1]] === undefined) {
      groupedData[key][row[1]] = {};
    }
    groupedData[key][row[1]][row[2]] = item.replace(`${row[0]},${row[1]},${row[2]},`, '');
  });

  // set the headers
  let parentHeaders = 'Metric,';
  let subHeaders = `${mapper[fileName].groupBy},`;
  Object.keys(metrics).forEach((metric) => {
    if (metric) {
      if (metric === 'undefined') return;
      // get the number of sub headers and merge the parent
      parentHeaders += metric + ','.repeat(metrics[metric].split(',').length - 1);
      subHeaders += metrics[metric];
    }
  });

  csv += `${parentHeaders}\r\n`;
  csv += `${subHeaders}\r\n`;
  // sort the array
  let sortedData;
  const groupedDataKeys = Object.keys(groupedData);
  // eslint-disable-next-line no-restricted-globals
  if (
    // eslint-disable-next-line no-restricted-globals
    !isNaN(
      Object.keys(groupedData)
        .splice(0, 10)
        .filter((key) => key !== '')[0]
    )
  ) {
    // eslint-disable-next-line arrow-body-style
    sortedData = groupedDataKeys.sort((a, b) => {
      return a - b;
    });
  } else {
    sortedData = groupedDataKeys.sort();
  }
  // make sure the data names are sorted properly
  if (fileName.includes('Week')) {
    sortedData = daysOrder;
  }

  const filteredGroupedDataKeys = groupedDataKeys.filter((str) => str !== '');

  if (isMonthYearPatternList(filteredGroupedDataKeys[0])) {
    sortedData = filteredGroupedDataKeys.sort((a, b) => {
      const [monthA, yearA] = a.split(' ');
      const [monthB, yearB] = b.split(' ');
      const yA = parseInt(yearA, 10);
      const yB = parseInt(yearB, 10);
      if (yA !== yB) return yA - yB;
      return monthsOrder.indexOf(monthA) - monthsOrder.indexOf(monthB);
    });
  }

  // concatenate the data
  sortedData.forEach((item) => {
    if (item) {
      csv += item;
      Object.keys(groupedData[item]).forEach((m) => {
        // if values are not inline to the columns this logic could be the issue
        const currentMetrics = Object.keys(groupedData[item][m]);
        // mark the number of the iteration and look if the values for that index is there in the current metric list
        // if not add a placeholder to reserve that space
        metrics[m]
          .slice(0, -1)
          .split(',')
          .forEach((v) => {
            // check for empty values where the columns are defined but the value is absent
            if (currentMetrics.includes(v) && v !== 'undefined') {
              // append the value to csv
              csv += `,${groupedData[item][m][v]}`;
            } else {
              csv += `,""`;
            }
          });
      });
      csv += '\r\n';
    }
  });

  return csv;
};

const withDoubleHeaders = (result, fileName) => {
  // Split data set by new line character and create a new array removing the first row
  const parsedData = result?.data.split('\r\n').slice(1);
  const metrics = Object.keys(mapper[fileName].metrics);
  // convert the metric label to mapper key format eg: "Room Nights" -> Room_Nights
  const normalizeMetric = (value) => (value ?? '').replaceAll('"', '').trim().replaceAll(' ', '_');
  let csv = '';
  // set the parent headers
  csv += `Metric,`;
  metrics.forEach((metric) => {
    csv += metric + ','.repeat(Object.keys(mapper[fileName].metrics[metric].subColumns).length);
  });
  csv += '\r\n';
  // set the sub headers
  csv += `${mapper[fileName].groupBy},`;
  metrics.forEach((metric) => {
    csv += `${mapper[fileName].metrics[metric].subColumns.join(',')},`;
  });
  // and a new line to the end
  csv += '\r\n';

  const groupedDataByMetric = {};
  // populate the array with the groups
  metrics.forEach((metric) => {
    groupedDataByMetric[metric] = [];
  });

  parsedData.forEach((row) => {
    let rowData = [];
    let currentVal = '';
    let withinQuotes = false;

    for (let i = 0; i < row.length; i += 1) {
      const char = row[i];
      // add to add this to add a blank when columns like forecast are blank to occupy space for that
      if (char === ',' && !withinQuotes) {
        rowData.push(`"${currentVal.trim()}"`);
        currentVal = '';
      } else if (char === '"') {
        withinQuotes = !withinQuotes;
      } else {
        currentVal += char;
      }
    }
    // Push the last value after the loop
    rowData.push(currentVal.trim());

    // the labels may be in not in same order in different but similar visuals
    // eg: TVH_* -> Date,A Actls,B Actls,% Chg,A Fcsts,B Fcsts,Metric
    //     PVH-* -> Date,Label,A Actls,B Actls,% Chg,A Fcsts,B Fcsts
    // so locate the metric column by its value, falling back to the positional lookup
    let groupIndex = rowData.findIndex(
      (rowValue, index) => index > 0 && metrics.includes(normalizeMetric(rowValue))
    );
    if (groupIndex === -1) {
      groupIndex = mapper[fileName].transformColumns ? 1 : rowData.length - 1;
    }
    let group = rowData.splice(groupIndex, 1)[0];
    if (group) {
      group = normalizeMetric(group);
      // skip rows with unknown metrics instead of failing the whole export
      if (!groupedDataByMetric[group]) return;
      // make sure the currency values are wrapped with "" all the time
      rowData = rowData.map((rowValue) => {
        if (rowValue.includes(',') && !rowValue.includes('"')) {
          return `"${rowValue}"`;
        }
        return rowValue;
      });
      groupedDataByMetric[group].push(rowData);
    }
  });
  // merge data by groups
  const concatenatedData = {};

  metrics.forEach((metric) => {
    groupedDataByMetric[metric].forEach((item) => {
      // format the - separated date time after checking if the value is datetime
      const key = formatIfValueIsDate(item[0]);

      if (concatenatedData[key] === undefined) {
        // concatenate the line using the date as the key
        // condition to prevent concatenating to undefined value
        concatenatedData[key] = '';
      }

      // check if the item contain more columns than the desired
      // eg: for forecast a "" is appended to every metric
      while (mapper[fileName].metrics[metric].subColumns.length !== item.slice(1).length) {
        item.pop();
      }
      concatenatedData[key] += `${item.slice(1).join(',')},`;
    });
  });

  // append data to the csv
  Object.keys(concatenatedData).forEach((item) => {
    csv += `${item},${concatenatedData[item]}\r\n`;
  });

  csv = csv.replaceAll('metrics_spot_MA', 'Selection');

  // strip _ from mapper values preventing column names with _ in the export
  Object.keys(mapper[fileName].metrics).forEach((m) => {
    csv = csv.replaceAll(m, m.replaceAll('_', ' '));
  });

  return csv;
};

const GenerateCSV = (result, fileName) => {
  switch (mapper[fileName].headerType) {
    case VISUAL_HEADER_TYPES.SINGLE:
      return withSingleHeader(result, fileName);
    case VISUAL_HEADER_TYPES.SINGLE_2:
      return result?.data ?? '';
    case VISUAL_HEADER_TYPES.DOUBLE:
      return withDoubleHeaders(result, fileName);
    case VISUAL_HEADER_TYPES.DOUBLE_2:
      return withDoubleHeaders2(result, fileName);
    case VISUAL_HEADER_TYPES.GROUPED_DOUBLE:
      return withGroupedDoubleHeaders(result, fileName);
    default:
      return '';
  }
};
//
const extractAllMetaFromPBI = async (visualComponents) => {
  let detailsList = [];
  detailsList = await Promise.all(
    visualComponents.map(async (component) => {
      let data = await component.exportData(models.ExportDataType.Summarized);
      data = data.data.split('\r\n').filter((dataComponent) => dataComponent.includes(':'));
      return data[0];
    })
  );

  return detailsList;
};
/**
 * Converts an array of arrays (AOA) into a CSV string.
 *
 * - Joins values with commas
 * - Escapes values containing commas, quotes, or newlines
 * - Removes fully empty rows
 *
 * @param {Array<Array<string | number | null | undefined>>} aoa - Table data as rows and columns
 * @returns {string} CSV formatted string
 */
export const aoaToCsv = (aoa) => {
  if (!aoa || !aoa.length) return '';

  return aoa
    .filter((row) => row.length && row.some((cell) => cell !== '')) // remove empty rows
    .map((row) =>
      row
        .map((cell) => {
          const value = cell ?? '';

          // Escape if contains comma, quote, or newline
          if (/[",\r\n]/.test(value)) {
            return `"${value.replace(/"/g, '""')}"`;
          }

          return value;
        })
        .join(',')
    )
    .join('\r\n'); // CSV standard line break
};
/**
 * Builds Excel data from Power BI report.
 *
 * @param {Object} params
 * @returns {Promise<{ aoa: string[][], workbookData: string }>}
 */
export const buildPBIExcelData = async ({
  report,
  currentUser,
  selectedHotel,
  metaData,
  isInterPreterAgent = false,
}) => {
  const pages = await report.getPages();
  const currentPage = pages.find((page) => page.isActive);

  const visuals = await currentPage.getVisuals();

  const visualDetailsComponents = visuals.filter((visual) => visual.type === 'multiRowCard');

  let visualMeta = metaData ?? (await extractAllMetaFromPBI(visualDetailsComponents));

  const tableVisual = visuals.find((v) => v.type === 'tableEx');
  console.log(tableVisual);
  const result = await tableVisual.exportData(models.ExportDataType.Summarized);

  result.data = result?.data.replaceAll(', ', '####');

  // Process meta data
  visualMeta = visualMeta.join(',').replaceAll(',', '\r\n').replaceAll(':', ',');

  let workbookData = isInterPreterAgent ? '' : `Username, ${currentUser?.username}\r\n`;
  workbookData += isInterPreterAgent
    ? ''
    : `Report Generation Date, ${formatDate(new Date(), YEAR_MONTH_DAY_PATTERN)} ${formatDate(
        new Date(),
        TIME_PATTERN
      )}\r\n`;
  workbookData += `Hotel Name, ${selectedHotel?.label}\r\n`;
  workbookData += `${visualMeta} \r\n\r\n`;

  const aoa = result?.data
    .split('\r\n')
    .map((row) =>
      row.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map((cell) => cell.replace(/^"|"$/g, ''))
    );

  return { aoa, workbookData };
};

export default GenerateCSV;
